/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const ProjectAccept: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [projectNGO, setProjectNGO] = useState<any>(null);
  const [requestChangesComment, setRequestChangesComment] = useState('');
  const [projectMetrics, setProjectMetrics] = useState<any[]>([]);

  useEffect(() => {
    if (projectId && ngoId) {
      loadProject();
      loadProjectNGO();
    }
  }, [projectId, ngoId]);

  const loadProject = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (!error && data) {
      setProject(data);
    }

    // Load metrics from project_metrics_progress
    const { data: metricsData, error: metricsError } = await supabase
      .from('project_metrics_progress')
      .select('*')
      .eq('project_id', projectId);

    if (!metricsError && metricsData) {
      setProjectMetrics(metricsData);
    }
  };

  const loadProjectNGO = async () => {
    if (!projectId || !ngoId) return;

    const { data, error } = await supabase
      .from('project_ngos')
      .select('*')
      .eq('project_id', projectId)
      .eq('ngo_id', ngoId)
      .single();

    if (!error && data) {
      setProjectNGO(data);
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('project_ngos')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString()
        })
        .eq('project_id', projectId)
        .eq('ngo_id', ngoId);

      if (updateError) throw updateError;

      // Navigate to project details so NGO can start filling information
      navigate(`/ngo/ngo/projects/${projectId}/details`);
    } catch (err: any) {
      setError(err.message || 'Failed to accept project');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!requestChangesComment.trim()) {
      setError('Please provide a comment explaining the requested changes');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('project_ngos')
        .update({
          status: 'changes_requested',
          requested_changes: requestChangesComment
        })
        .eq('project_id', projectId)
        .eq('ngo_id', ngoId);

      if (updateError) throw updateError;

      // Update project status back to 'draft' for company to edit
      await supabase
        .from('projects')
        .update({ status: 'draft' })
        .eq('id', projectId);

      navigate(`/ngo/ngo/projects`);
    } catch (err: any) {
      setError(err.message || 'Failed to request changes');
    } finally {
      setLoading(false);
    }
  };

  if (!project || !projectNGO) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (projectNGO.status !== 'pending') {
    return (
      <div className="form-container">
        <div className="form-section">
          <h1>Project Status</h1>
          <div className="card">
            <p>
              This project has already been {projectNGO.status === 'accepted' ? 'accepted' : 'changes requested'}.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/ngo/ngo/projects/${projectId}`)}
            >
              View Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if project has minimal data (only name and summary)
  const hasMinimalData = !project.thematic_area && !project.start_date && projectMetrics.length === 0;

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Project Assignment</h1>
        {hasMinimalData ? (
          <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
            This project has been assigned to you. Please fill in all project details.
          </p>
        ) : (
          <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
            Review the project proposal and accept or request changes
          </p>
        )}

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="card">
          <h2>{project.name}</h2>
          <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>{project.summary}</p>

          {hasMinimalData && (
            <div style={{ 
              background: '#eff6ff', 
              border: '1px solid #3b82f6', 
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, color: '#1e40af' }}>
                <strong>Action Required:</strong> This project needs all details to be filled. Click the button below to complete the project information.
              </p>
            </div>
          )}

          <div className="form-row">
            <div>
              <strong>Thematic Area:</strong>
              <p>{project.thematic_area}</p>
            </div>
            <div>
              <strong>Reporting Cadence:</strong>
              <p>{project.reporting_cadence}</p>
            </div>
          </div>

          {project.start_date && project.end_date && (
            <div>
              <strong>Duration:</strong>
              <p>
                {new Date(project.start_date).toLocaleDateString()} - {new Date(project.end_date).toLocaleDateString()}
              </p>
            </div>
          )}

          {project.budget && (
            <div>
              <strong>Budget:</strong>
              <p>₹{parseFloat(project.budget).toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {project.proposal_content && (
          <div className="card">
            <h2>Proposal Content</h2>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
              {project.proposal_content}
            </div>
          </div>
        )}

        {projectMetrics.length > 0 && (
          <div className="card">
            <h2>Metrics & Targets</h2>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Target</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {projectMetrics.map((metric: any, index: number) => (
                    <tr key={index}>
                      <td>{metric.metric_name}</td>
                      <td>{metric.target_value}</td>
                      <td>{metric.unit || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {hasMinimalData ? (
          <div className="card">
            <h2>Fill Project Details</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>
              You need to fill in all project details including thematic area, dates, locations, metrics, budget, and proposal content.
            </p>
            <div className="form-actions">
              <Link
                to={`/ngo/ngo/projects/${projectId}/details`}
                className="btn btn-primary"
              >
                Fill Project Details
              </Link>
            </div>
          </div>
        ) : (
          <div className="card">
            <h2>Your Decision</h2>
            
            <div className="form-group">
              <label>Request Changes Comment (if requesting changes)</label>
              <textarea
                value={requestChangesComment}
                onChange={(e) => setRequestChangesComment(e.target.value)}
                placeholder="Explain what changes you need..."
                rows={4}
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate(`/ngo/ngo/dashboard`)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleRequestChanges}
                disabled={loading}
              >
                Request Changes
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAccept}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Accept Project'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectAccept;
